import {
  Button,
  ButtonColor,
  ButtonSize,
  Combobox,
  Option,
  Table,
  TableRow,
} from '@code/innovance-react-ui-kit';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { CardObservation, Product } from 'src/front/api/apiInstance';
import { ObservationVisibilityResponse } from 'src/front/api/generated';
import useDeviceType from 'src/front/common/hooks/useDeviceType';

import useYupResolver from 'src/front/common/hooks/useYupResolver';
import { formatDate } from 'src/front/common/utils/dateTimeUtils';
import { optionFormat } from 'src/front/common/utils/formUtils';
import { CardObservationSchema } from 'src/front/common/validations/cardObservation';
import AsyncButton from 'src/front/components/AsyncButton';
import Datepicker from 'src/front/components/Datepicker';

import {
  Form,
  FormElement,
  FormRow,
  Separator,
  Subtitle,
  TableWrapper,
  Title,
} from 'src/front/components/FormStyles';
import { CARD_APPLICATON_STATUS, TABLE_WIDTHS } from 'src/front/constant';

import {
  useClearDispatch,
  useCustomerSearchDispatch,
  useCustomerSearchState,
} from 'src/front/contexts/CardObservationContext';
import { useUserInfoState } from 'src/front/contexts/SessionContext';
import styled from 'styled-components';

const CornerFormElement = styled.div`
  display: flex;
  justify-content: flex-end;

  margin-top: 15px;
`;

export interface CardSearchFormData {
  beginDate: Date | null;
  endDate: Date | null;
  pos: Option | null;
}

const defaultValues = {
  beginDate: null,
  endDate: null,
  pos: null,
};

const cardsTableHeader = [
  'cardObservation_clientName_text',
  'cardObservation_status_text',
  'cardObservation_applicationDate_text',
  'cardObservation_entryDate_text',
  'cardObservation_referenceNumber_text',
  'cardObservation_limit_text',
  'cardObservation_debit_text',
  'cardObservation_clientType_text',
  'cardObservation_currency_text',
];

export interface TableCardObservationRow extends TableRow {
  status?: string;
  customerNumber?: string;
  cardId?: string;
}

interface Props {
  goToNextStep: (data: ObservationVisibilityResponse) => void;
}

const CardObservationSearch: FC<Props> = ({ goToNextStep }) => {
  const { isTablet } = useDeviceType();
  const dispatch = useCustomerSearchDispatch();
  const cardSearchContext = useCustomerSearchState();
  const clearCustomerSearch = useClearDispatch();
  const { processDate } = useUserInfoState();
  const { t } = useTranslation();

  const resolver = useYupResolver(CardObservationSchema, { todayMaxControl: true });

  const [pointOfSales, setPointOfSales] = useState<Option[]>([]);

  const [showResult, setShowResult] = useState<boolean>(false);
  const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false);
  const [selectedCard, setSelectedCard] = useState<TableCardObservationRow | undefined>(undefined);
  const [cardList, setCardList] = useState<TableRow[]>([]);
  const [page, setPage] = useState<number>(1);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);

  const {
    watch,
    register,
    errors,
    control,
    setValue,
    handleSubmit,
    formState,
    getValues,
  } = useForm<CardSearchFormData>({
    defaultValues: {
      ...defaultValues,
      beginDate: processDate || null,
      endDate: processDate || null,
    },
    resolver,
  });

  useEffect(() => {
    (async () => {
      const { data } = await Product.getPOSData();
      setPointOfSales(data.posDataList.map(optionFormat));
    })();
  }, []);

  useEffect(() => {
    register({ name: 'beginDate' });
    register({ name: 'endDate' });
  }, [register]);

  const loadCardList = useCallback(
    async (pageNumber?: number, forceRefresh = false): Promise<TableCardObservationRow[]> => {
      setShowResult(false);
      try {
        const { beginDate, endDate, pos } = getValues();
        setIsLoadingTable(true);
        const { data } = await CardObservation.getCardObservationCards({
          beginDate: formatDate(beginDate),
          endDate: formatDate(endDate),
          posId: pos?.value || '',
          rowCountPerPage: 20,
          pageNumber: pageNumber || page,
          refresh: !pageNumber || forceRefresh,
        });
        const cards = data.cardList.map((card) => ({
          id: card.referenceNumber,
          columns: [
            card.cardOwner || '',
            card.cardStatus,
            card.applicationDate || '',
            card.entryDate || '',
            card.referenceNumber,
            card.cardLimit,
            card.cardType || '',
            card.customerType || '',
            card.currency || '',
          ],
          status: card.cardStatus,
          customerNumber: card.customerNumber,
          cardId: card.cardId,
        }));

        setShowResult(true);
        setCardList(cards);

        setHasNextPage(data.hasNextPage);
        setPage(pageNumber || 1);
        setIsLoadingTable(false);

        return cards;
      } catch (error) {
        // TODO (mertohancan): show error on modal or handle with error boundary
        return [];
      }
    },
    [getValues, page]
  );

  const loadPageItems = useCallback(
    async (pageNumber?: number): Promise<void> => {
      loadCardList(pageNumber);
    },
    [loadCardList]
  );

  const handleDateChange = (fieldName: string) => (date: Date): void =>
    setValue(fieldName, date, {
      shouldValidate: formState.isSubmitted || !!date,
      shouldDirty: formState.isSubmitted || !!date,
    });

  const handleSelectCard = (card: TableCardObservationRow | undefined): void =>
    setSelectedCard(card);

  const onSearchSubmit = (): void => {
    clearCustomerSearch();
    selectedCard && setSelectedCard(undefined);
    loadCardList(1, true);
  };

  const onCardSubmit = async (): Promise<void> => {
    const { beginDate, endDate, pos } = getValues();

    const { data } = await CardObservation.getCardObservationButtonVisibility({
      referenceNumber: selectedCard?.id || '',
      pageNumber: page,
    });
    if (data && data.result === 'OK') {
      goToNextStep(data);
      dispatch({
        beginDate,
        endDate,
        pos,
        customerSearchValues: selectedCard,
        page,
      });
    }
  };

  useEffect(() => {
    if (cardSearchContext.customerSearchValues) {
      setValue('beginDate', cardSearchContext.beginDate);
      setValue('endDate', cardSearchContext.endDate);
      setValue('pos', cardSearchContext.pos);
      loadPageItems(cardSearchContext.page);
      setSelectedCard(cardSearchContext.customerSearchValues);
    }
  }, [
    cardSearchContext.beginDate,
    cardSearchContext.customerSearchValues,
    cardSearchContext.endDate,
    cardSearchContext.page,
    cardSearchContext.pos,
    loadPageItems,
    setValue,
  ]);

  return (
    <>
      <Title>
        <Trans i18nKey="cardObservation_title" />
      </Title>
      <Separator />
      <form onSubmit={handleSubmit(onSearchSubmit)}>
        <FormRow>
          <FormElement>
            <Datepicker
              errorMessage={t(errors?.beginDate?.message as string)}
              inputLabel={`${t('cardObservation_startDate_label')} *`}
              onChange={handleDateChange('beginDate')}
              value={watch('beginDate') as Date}
            />
          </FormElement>
          <FormElement>
            <Datepicker
              errorMessage={t(errors?.endDate?.message as string)}
              inputLabel={`${t('cardObservation_endDate_label')} *`}
              onChange={handleDateChange('endDate')}
              value={watch('endDate') as Date}
            />
          </FormElement>
        </FormRow>

        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="pos"
              render={({ onChange, value }) => (
                <Combobox
                  errorMessage={t(errors?.pos?.message as string)}
                  items={pointOfSales}
                  label={t('cardObservation_pos_label')}
                  name="pos"
                  onChange={onChange}
                  value={value}
                  required
                />
              )}
            />
          </FormElement>
          <FormElement>
            <CornerFormElement>
              <Button
                color={ButtonColor.Primary}
                disabled={formState.isSubmitting}
                size={ButtonSize.Medium}
                text={t('general_components_search_button')}
                type="submit"
              />
            </CornerFormElement>
          </FormElement>
        </FormRow>
      </form>
      {showResult ? (
        <>
          <Separator />
          <Subtitle>
            <Trans i18nKey="cardObservation_results_text" />
          </Subtitle>
          <Form>
            <TableWrapper>
              <Table
                emptyTableText={t('cardEntry_customerInfo_notFound_text')}
                hasNextPage={hasNextPage}
                headers={cardsTableHeader.map((head) => t(head))}
                items={cardList}
                loading={isLoadingTable}
                loadPageItems={loadPageItems}
                onSelect={handleSelectCard}
                pageNumber={page}
                selectedItem={selectedCard}
                tableWidth={isTablet ? TABLE_WIDTHS.TABLET : TABLE_WIDTHS.DESKTOP_2X}
                widths={['18', '11', '11', '10', '10', '11', '11', '11', '7']}
                clearItemOnPageChange
                withRadio
              />
            </TableWrapper>
          </Form>
        </>
      ) : null}

      {selectedCard?.status === CARD_APPLICATON_STATUS.APPROVED ? (
        <FormRow>
          <FormElement>
            <AsyncButton
              color={ButtonColor.Primary}
              onClick={onCardSubmit}
              size={ButtonSize.Medium}
              text={t('general_continue_button')}
              type="button"
            />
          </FormElement>
        </FormRow>
      ) : null}
    </>
  );
};

export default CardObservationSearch;
